'use client'

import type { DialogAPI } from '@fukku/dialog'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useProductCardConsumerLayoutContext } from 'product-card/hooks/useProductCardConsumerLayoutContext'
import { useProductCardContext } from 'product-card/hooks/useProductCardContext'
import { useProductColors } from 'product-card/hooks/useProductColors'
import type { Product } from 'product/types'
import { useRef } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import type { PlpDomainData } from 'types/masterData/domainData/domainData'

import { appsAddToBag } from '../../../../utils/appsAddToBag/appsAddToBag'
import { AddToBagOneSizeButton } from '../AddToBag/AddToBagOneSizeButton'

import { ButtonPrimary } from '@fukku/button/Primary'
import { ButtonSecondary } from '@fukku/button/Secondary'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { SizesBottomSheet } from 'product-components/components/SizeSelector/SizesBottomSheet/SizesBottomSheet'
import { useAddSelectedSize } from '../../../../hooks/useAddSelectedSize/useAddSelectedSize'
import styles from './SizeSheetButton.module.scss'

interface AddToBagButtonProps {
	readonly product: Product
}

export const SizeSheetButton = ({ product }: AddToBagButtonProps) => {
	const { isApp, custom } = useMasterData()
	const sizeSheetRef = useRef<DialogAPI>(null)

	const { isLessThanLarge } = useResponsive()
	const { hasSizeSheetButton } = useProductCardConsumerLayoutContext()
	const { productId, selectedColorId, lookId, location } =
		useProductCardContext()

	const { productColors } = useProductColors({ product })

	const { shouldRenderAddButtonAsPrimary } =
		useProductCardConsumerLayoutContext()
	const { t } = useLabels()

	const ButtonComponent = shouldRenderAddButtonAsPrimary
		? ButtonPrimary
		: ButtonSecondary

	const productColorInfo = productColors?.find(
		(productColor) => productColor.id === selectedColorId
	)

	const hasSizes = productColorInfo && productColorInfo.sizes.length > 1
	const isOneSize = productColorInfo?.sizes.length === 1

	const { onAdd } = useAddSelectedSize({
		product,
		hasSizes,
		opened: sizeSheetRef.current?.open ?? false,
	})

	if (!productColorInfo) {
		return null
	}

	const shouldRenderSizes = hasSizeSheetButton && isLessThanLarge

	const onOpen = (): undefined => {
		if (isApp) {
			appsAddToBag(product.id, selectedColorId, lookId, custom as PlpDomainData)
		} else {
			sizeSheetRef.current?.show()
		}
	}

	if (shouldRenderSizes) {
		if (hasSizes) {
			return (
				<div className={styles.sizeSheetButtonContainer}>
					<ButtonComponent
						fullWidth
						data-testid='productCard.sizeSheetButton'
						onClick={onOpen}
					>
						{t('productCard.showSizes.button')}
					</ButtonComponent>
					<SizesBottomSheet
						ref={sizeSheetRef}
						product={product}
						productId={productId}
						selectedColorId={selectedColorId}
						onSelectedSize={onAdd}
						location={location}
					/>
				</div>
			)
		}

		if (isOneSize) {
			return (
				<div className={styles.sizeSheetButtonContainer}>
					<AddToBagOneSizeButton size={productColorInfo.sizes[0]} />
				</div>
			)
		}
	}

	return null
}
